import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Rinnovo della sessione: l'access token dura 24 ore, il refresh 7 giorni.
// Una sola chiamata a /auth/refresh anche se più richieste falliscono insieme.
let refreshing = null;
const renewSession = () => {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, null, { _noRenew: true })
      .then(() => true, () => false)
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
};
const isAuthCall = (url = "") => /\/auth\/(refresh|logout|me|admin\/login|tenants\/[^/]+\/(login|register|forgot))/.test(url)
  || url.includes("/auth/reset-password") || url.includes("/auth/password") || url.includes("/admin/account");

const AuthContext = createContext({
  user: null, loading: true,
  loginTenant: async () => {}, registerTenant: async () => {},
  loginAdmin: async () => {}, logout: async () => {}, refresh: async () => {},
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      // access token scaduto: si prova a rinnovare la sessione col refresh token
      if (e?.response?.status === 401 && await renewSession()) {
        try {
          const { data } = await axios.get(`${API}/auth/me`);
          setUser(data);
          return;
        } catch (_e2) { /* sessione non più valida */ }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Se una chiamata risponde 401 a metà giornata (token scaduto), si rinnova la
  // sessione e la si ripete una volta; se il rinnovo fallisce si torna al login.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, async (error) => {
      const cfg = error?.config;
      if (error?.response?.status !== 401 || !cfg || cfg._noRenew || cfg._retried || isAuthCall(cfg.url)) {
        return Promise.reject(error);
      }
      if (await renewSession()) {
        return axios({ ...cfg, _retried: true });
      }
      setUser(null);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const loginTenant = useCallback(async (slug, email, password) => {
    const { data } = await axios.post(`${API}/auth/tenants/${slug}/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const registerTenant = useCallback(async (slug, payload) => {
    const { data } = await axios.post(`${API}/auth/tenants/${slug}/register`, payload);
    setUser(data);
    return data;
  }, []);

  const loginAdmin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/admin/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error("AuthContext: logout request failed", e);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginTenant, registerTenant, loginAdmin, logout, refresh }),
    [user, loading, loginTenant, registerTenant, loginAdmin, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
